import { marketSessionDate } from './marketSessionDate';
import {
  etMinutesOfDay,
  isUsEquityTradingDate,
  marketDataSessionDate,
  nextUsEquitySessionDate,
  usEquityCloseMinutes,
} from './usEquityCalendar';

export {
  setPublishedMarketClosures,
  clearPublishedMarketClosures,
  setPublishedMarketEarlyCloses,
  clearPublishedMarketEarlyCloses,
} from './usEquityCalendar';

/**
 * Which US-equity session is running right now.
 *
 * `pre` and `after` are the extended sessions in which PRICES move but the options tape does not.
 * `closed` covers overnight, weekends and exchange holidays alike — from the viewer's side those
 * are one state ("nothing is updating"), and splitting them would only ask a question the reader
 * did not have.
 */
export type MarketSessionPhase = 'pre' | 'regular' | 'after' | 'closed';

export const PRE_MARKET_OPEN_MINUTES = 4 * 60; // 4:00 AM ET
export const REGULAR_OPEN_MINUTES = 9 * 60 + 30; // 9:30 AM ET

/**
 * The extended session runs four hours past the bell, not to a fixed 8:00 PM.
 *
 * A constant 20:00 is wrong on every early-close day: on the half-days the exchange calendar
 * already knows about (day after Thanksgiving, July 3, a weekday Dec 24) the cash session ends at
 * 1:00 PM and the tape genuinely stops at 5:00 PM — verified against two production half-days,
 * where the last dark-pool print landed at 17:00:06 and 17:00:07 ET respectively. Deriving the
 * extended close from the cash close is what keeps a violet "After-hours" dot off a market that
 * shut three hours earlier.
 */
export const EXTENDED_SESSION_MINUTES = 4 * 60;

export const extendedCloseMinutes = (dateIso: string): number =>
  usEquityCloseMinutes(dateIso) + EXTENDED_SESSION_MINUTES;

/**
 * Classify one instant. Invalid instants fail closed — a grey dot understates a running market,
 * which is the safe direction for a control whose whole job is to be trusted.
 */
export const marketSessionPhase = (nowMs: number): MarketSessionPhase => {
  if (!Number.isFinite(nowMs)) return 'closed';

  const today = marketSessionDate(nowMs);
  if (!isUsEquityTradingDate(today)) return 'closed';

  const minutes = etMinutesOfDay(nowMs);
  if (!Number.isFinite(minutes)) return 'closed';

  if (minutes >= PRE_MARKET_OPEN_MINUTES && minutes < REGULAR_OPEN_MINUTES) return 'pre';

  const close = usEquityCloseMinutes(today);
  if (minutes >= REGULAR_OPEN_MINUTES && minutes < close) return 'regular';
  if (minutes >= close && minutes < extendedCloseMinutes(today)) return 'after';

  return 'closed';
};

/** True while prices are moving — the three sessions in which a live spot dot should be lit. */
export const isPriceSessionOpen = (phase: MarketSessionPhase): boolean => phase !== 'closed';

/**
 * True when a trade printed at `tradeMs` came after the latest cash-session close as of `nowMs`:
 * outside any regular session, and on the far side of the same close as now. During a regular
 * session nothing qualifies, because the close it measures from has not happened yet.
 *
 * Extended-hours trades are minutes apart, so a page outside regular hours shows the last one
 * printed since the close rather than waiting for a quote that is seconds old.
 */
export const isTradeSinceLatestClose = (tradeMs: number, nowMs: number): boolean =>
  Number.isFinite(tradeMs) &&
  marketSessionPhase(nowMs) !== 'regular' &&
  marketSessionPhase(tradeMs) !== 'regular' &&
  marketDataSessionDate(tradeMs) === marketDataSessionDate(nowMs);

const WEEKDAY_FORMATTER = new Intl.DateTimeFormat('en-US', {
  timeZone: 'UTC',
  weekday: 'long',
});

const weekdayOf = (dateIso: string): string =>
  WEEKDAY_FORMATTER.format(new Date(`${dateIso}T12:00:00Z`));

/**
 * The weekday to name in "Coverage restarts {weekday} at 4:00 AM ET", or `null` when coverage
 * restarts later today.
 *
 * The null case is the one that matters: between midnight and 4:00 AM on a trading day the next
 * open IS today, and naming a weekday there would put the reader a full day out.
 */
export const nextCoverageWeekday = (nowMs: number): string | null => {
  if (!Number.isFinite(nowMs)) return null;
  const today = marketSessionDate(nowMs);
  if (isUsEquityTradingDate(today) && etMinutesOfDay(nowMs) < PRE_MARKET_OPEN_MINUTES) return null;
  return weekdayOf(nextUsEquitySessionDate(today));
};

/**
 * The weekday to name for the next options-tape open, or `null` when the tape opens later today.
 *
 * Distinct from `nextCoverageWeekday` because the tape opens at 9:30 while price coverage starts at
 * 4:00 — during pre-market the tape opens today, and after the bell it does not.
 */
export const nextTapeWeekday = (nowMs: number): string | null => {
  if (!Number.isFinite(nowMs)) return null;
  const today = marketSessionDate(nowMs);
  if (isUsEquityTradingDate(today) && etMinutesOfDay(nowMs) < REGULAR_OPEN_MINUTES) return null;
  return weekdayOf(nextUsEquitySessionDate(today));
};
